import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useDeferredValue,
} from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button as MuiButton,
  Chip,
  Dialog,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../components";
import { baseAxios } from "../../utils/config";
import { colors } from "..";
import DistributorForm from "./distributor-form";

const STATUS_OPTIONS = [
  { value: true, label: "Active" },
  { value: false, label: "Inactive" },
];

const MARK_STYLE = {
  backgroundColor: "#fff882",
  color: "inherit",
  borderRadius: 4,
};

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${tokens.join("|")})`, "gi");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function HeaderColumnFilter({
  ariaLabel,
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  options,
  allMenuLabel,
}) {
  const active = value !== "" && value != null;
  return (
    <Box
      component="span"
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: "inline-flex", alignItems: "center", ml: 0.25 }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl((prev) =>
            prev === e.currentTarget ? null : e.currentTarget
          );
        }}
        sx={{
          p: 0.35,
          color: active ? "primary.main" : colors.white,
          opacity: active ? 1 : 0.85,
          "&:hover": {
            color: colors.white,
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { maxWidth: 360, mt: 0.5 },
          },
        }}
      >
        <MenuItem
          selected={value === ""}
          onClick={() => {
            onChange("");
            setAnchorEl(null);
          }}
        >
          {allMenuLabel}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            selected={value === opt.value}
            onClick={() => {
              onChange(opt.value);
              setAnchorEl(null);
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function distributorMatchesSearch(row, rawQuery) {
  const q = String(rawQuery ?? "").trim().toLowerCase();
  if (!q) return true;

  const blob = [row.reseller_company_name]
    .map((x) => String(x ?? "").toLowerCase())
    .join(" ");

  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.every((tok) => blob.includes(tok));
}

function compareDistributorRows(a, b, field) {
  const va = a[field];
  const vb = b[field];

  if (field === "is_active") {
    const na = va === true ? 1 : 0;
    const nb = vb === true ? 1 : 0;
    if (na !== nb) return na - nb;
  } else {
    const sa = va == null ? "" : String(va);
    const sb = vb == null ? "" : String(vb);
    const primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
    if (primary !== 0) return primary;
  }

  return (Number(a.reseller_id) || 0) - (Number(b.reseller_id) || 0);
}

export default function ManageDistributors() {
  const [distributorDialogOpen, setDistributorDialogOpen] = useState(false);
  const [dialogResellerId, setDialogResellerId] = useState(null);

  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const [filterStatus, setFilterStatus] = useState("");
  const [statusFilterMenuAnchor, setStatusFilterMenuAnchor] = useState(null);
  const [sortBy, setSortBy] = useState("reseller_company_name");
  const [sortOrder, setSortOrder] = useState("asc");

  const openCreateDistributor = useCallback(() => {
    setDialogResellerId(null);
    setDistributorDialogOpen(true);
  }, []);

  const openEditDistributor = useCallback((resellerId) => {
    setDialogResellerId(resellerId);
    setDistributorDialogOpen(true);
  }, []);

  const closeDistributorDialog = useCallback(() => {
    setDistributorDialogOpen(false);
  }, []);

  const handleDistributorDialogExited = useCallback(() => {
    setDialogResellerId(null);
  }, []);

  const loadDistributors = useCallback(() => {
    setLoading(true);
    baseAxios
      .get("/resellers")
      .then((result) => {
        const data = result.data;
        const list = data.resellers || data.data?.resellers || data;
        setDistributors(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setDistributors([]);
        toast.error("Could not load distributors.", { duration: 3000 });
      })
      .finally(() => setLoading(false));
  }, []);

  const onDistributorSaved = useCallback(() => {
    loadDistributors();
    closeDistributorDialog();
  }, [loadDistributors, closeDistributorDialog]);

  useEffect(() => {
    loadDistributors();
  }, [loadDistributors]);

  const filteredDistributors = useMemo(() => {
    return distributors.filter((row) => {
      if (!distributorMatchesSearch(row, deferredSearch)) return false;
      if (filterStatus !== "" && row.is_active !== filterStatus) return false;
      return true;
    });
  }, [distributors, deferredSearch, filterStatus]);

  const sortedDistributors = useMemo(() => {
    const copy = [...filteredDistributors];
    copy.sort((a, b) => {
      const c = compareDistributorRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredDistributors, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const columns = useMemo(() => {
    const sq = deferredSearch;
    return [
      {
        id: "reseller_company_name",
        label: "Distributor Name",
        field: "reseller_company_name",
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "is_active",
        label: "Status",
        field: "is_active",
        minWidth: 140,
        headerExtras: (
          <HeaderColumnFilter
            ariaLabel="Filter by status"
            value={filterStatus}
            onChange={setFilterStatus}
            anchorEl={statusFilterMenuAnchor}
            setAnchorEl={setStatusFilterMenuAnchor}
            allMenuLabel="All statuses"
            options={STATUS_OPTIONS}
          />
        ),
        render: (value) => (
          <Chip
            size="small"
            variant="outlined"
            color={value ? "success" : "default"}
            label={value ? "Active" : "Inactive"}
            sx={{ fontWeight: 500, lineHeight: 1 }}
          />
        ),
      },
      {
        id: "actions",
        label: "Actions",
        sortable: false,
        render: (_, row) => (
          <MuiButton
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={() => openEditDistributor(row.reseller_id)}
            sx={{
              minHeight: 24,
              py: 0.25,
              px: 1,
              textTransform: "none",
              fontSize: "0.75rem",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.text.primary
                  : colors.black,
              "& .MuiButton-startIcon": { mr: 0.5, color: "primary.main" },
            }}
          >
            Edit
          </MuiButton>
        ),
      },
    ];
  }, [
    deferredSearch,
    filterStatus,
    statusFilterMenuAnchor,
    openEditDistributor,
  ]);

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Manage Distributors
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View, search, and edit product distributors and status.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search distributor name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 280,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon
                    sx={{
                      fontSize: 20,
                      color: "grey.400",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: "grey.800",
                        backgroundColor: "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            aria-label="Refresh distributors"
            onClick={loadDistributors}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={openCreateDistributor}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            <AddIcon sx={{ mr: 0.75 }} /> Add Distributor
          </MuiButton>
        </Box>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={sortedDistributors}
        totalRowCount={sortedDistributors.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="reseller_company_name"
        initialOrder="asc"
        searchQuery={`${deferredSearch}|${filterStatus}`}
        clientSidePagination
        skeletonHeight="40px"
      />

      <Dialog
        open={distributorDialogOpen}
        onClose={closeDistributorDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        TransitionProps={{ onExited: handleDistributorDialogExited }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "min(92vh, 900px)",
            overflow: "hidden",
          },
        }}
      >
        <DistributorForm
          key={
            dialogResellerId == null
              ? "new-distributor"
              : `distributor-${dialogResellerId}`
          }
          resellerId={dialogResellerId}
          onSaved={onDistributorSaved}
          onRequestClose={closeDistributorDialog}
        />
      </Dialog>
    </Box>
  );
}
